import { useCallback, useEffect, useState } from 'react';

// Shared fetch/create/update/remove/loading state for one API-backed collection.
// `resource` is one of the api.js client's { list, create, update, remove } groups.
// `transformIn` maps a raw API record into the shape page components expect.
export function useCollection(resource, transformIn = (x) => x, enabled = true) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await resource.list();
      setItems(data.map(transformIn));
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource]);

  useEffect(() => { if (enabled) reload(); }, [reload, enabled]);

  const create = useCallback(async (body) => {
    const created = transformIn(await resource.create(body));
    setItems((prev) => [created, ...prev]);
    return created;
  }, [resource]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = useCallback(async (id, body) => {
    const updated = transformIn(await resource.update(id, body));
    setItems((prev) => prev.map((item) => (item.id === id ? updated : item)));
    return updated;
  }, [resource]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = useCallback(async (id) => {
    await resource.remove(id);
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, [resource]);

  return { items, setItems, loading, error, reload, create, update, remove };
}
